import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@kidshome/ui';
import { useFeature } from '@/entities/session';
import { useHero, HeroFigure, HeroAvatar } from '@/entities/hero';
import { Feature } from '@/shared/lib/features';
import { tPlayKey } from '@/shared/lib/i18n';
import { HeroPresenceVoice } from '@/widgets/hero-presence/HeroPresenceVoice';

/**
 * HeroPresence (ADR-0095 §6) — the one-line hero strip on the kid's Home day-calendar. It shrinks the
 * old buried companion card to a small {@link HeroSprite} thumbnail + the hero name + a chevron, the
 * whole row linking to `/hero`. When no hero is chosen yet it shows a gentle "Pick your hero →" nudge
 * (never guilt-framed). A thin inline bond bar reads progress with NO XP/coin glyph.
 *
 * Secondary surface: hidden when `Feature.Play` is off, a calm skeleton while loading, and renders
 * NOTHING on error (it never nags — the day still works).
 */
export function HeroPresence({ className }: { className?: string }): React.ReactElement | null {
  const { t } = useTranslation('play');
  const hasFeature = useFeature();
  const playEnabled = hasFeature(Feature.Play);
  // THE single hero state (ADR-0102) — the same source the `/hero` page reads, so the Home strip never
  // disagrees with the page about who the hero is, what form, what's worn, or the bond. The worn bust
  // reflects exactly what's equipped in the wardrobe (a hat put on there shows here immediately).
  const { hero: data, isPending, isError } = useHero();

  if (!playEnabled) return null;

  if (isPending) {
    return (
      <div className={cn('card flex items-center gap-3', className)} aria-hidden="true">
        <div className="size-14 shrink-0 animate-pulse rounded-full bg-surface-muted motion-reduce:animate-none" />
        <div className="flex flex-1 flex-col gap-2">
          <div className="h-3.5 w-28 animate-pulse rounded-pill bg-surface-muted motion-reduce:animate-none" />
          <div className="h-2 w-full animate-pulse rounded-pill bg-surface-muted motion-reduce:animate-none" />
        </div>
      </div>
    );
  }

  // Never nag on error — render nothing (the day is unaffected).
  if (isError) return null;

  // No hero yet → a gentle nudge into /hero (which opens onboarding).
  if (data === null) {
    return (
      <Link
        to="/hero"
        className={cn(
          'card flex items-center gap-3 outline-none transition-transform focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]',
          className,
        )}
      >
        <span className="ring-mini" aria-hidden="true">
          <svg className="ring-svg" viewBox="0 0 56 56" fill="none">
            <circle cx="28" cy="28" r="24" stroke="var(--lavender-tint)" strokeWidth="4" />
          </svg>
          <HeroFigure archetypeId={null} stageIndex={0} stageCount={1} className="face" />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-primary">
          {t('presence.pick')}
        </span>
        <span aria-hidden="true" className="text-lg text-text-muted">
          ›
        </span>
      </Link>
    );
  }

  // A DEGRADED hero (retired/legacy/unresolved archetype) renders a calm neutral presence — exactly as
  // HeroPage does — so the Home strip and the hero page never disagree for the same hero: a neutral
  // "your buddy" name, the neutral sprite, and NO "Form N of M" line (a degraded hero has a single form).
  const degraded = data.degraded;
  const stageLabel = tPlayKey(t, data.stage.nameKey);
  const heading = degraded
    ? t('hero.collection.current')
    : (data.name ?? tPlayKey(t, data.nameKey));
  // The endless dual-track bond LEVEL comes pre-derived on the shared state (`data.level`), so the meter +
  // ring read identically to the `/hero` page — never the finite-stage remaining (which can only show 0%).
  const bl = data.level;
  const pct = Math.round(bl.fraction * 100);
  // Accessible name for the whole row (the sprite is decorative).
  const label = t('presence.label', { name: heading, stage: stageLabel });
  // The bond-ring sweep: r=24 → circumference ≈ 150.8; offset shrinks as the bond fills.
  const ringCirc = 150.8;
  const ringOffset = ringCirc * (1 - pct / 100);
  // The hero's rank/stage name under the name (kind, no XP/coin glyph) — suppressed for a degraded hero.
  // The finite "Form N of M" counter is intentionally NOT shown to kids (the endless Level is the headline).
  const formLine = degraded ? null : stageLabel;

  return (
    // ONE merged card (ADR-0095): the hero identity row (links to /hero) over the hero's "says" bubble,
    // instead of two separate stacked cards.
    <div className={cn('card flex flex-col gap-2.5', className)}>
      <Link
        to="/hero"
        aria-label={label}
        className="-m-1 flex items-center gap-3 rounded-[inherit] p-1 outline-none transition-transform focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]"
      >
        {/* A small bond-ring badge cradling the hero — lavender sweep reads progress at a glance. */}
        <span className="ring-mini" aria-hidden="true">
          <svg className="ring-svg" viewBox="0 0 56 56" fill="none">
            <circle cx="28" cy="28" r="24" stroke="var(--lavender-tint)" strokeWidth="4" />
            <circle
              cx="28"
              cy="28"
              r="24"
              stroke="var(--lavender-soft)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={ringCirc}
              strokeDashoffset={ringOffset}
              transform="rotate(-90 28 28)"
            />
          </svg>
          {/* The WORN bust (40px, framed by the 48px bond ring) — reflects equipped headwear+face so
              Home matches the wardrobe. Degraded → the neutral sprite (no look), matching the row + page. */}
          <HeroAvatar
            archetypeId={degraded ? null : data.archetypeId}
            stageIndex={degraded ? 0 : data.stageIndex}
            look={degraded ? undefined : data.look}
            size={40}
            className="relative z-[2]"
          />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5" aria-hidden="true">
          <span className="truncate text-sm font-bold text-text">{heading}</span>
          {formLine && (
            <span className="truncate text-xs font-semibold text-text-muted">{formLine}</span>
          )}
          {/* The ✨-level line (ADR-0103): the progress meter + the "Level N" label (the headline progression). */}
          <div className="mt-0.5 flex items-center gap-2">
            <span className="meter h-1.5 flex-1" role="presentation">
              <span className="meter-fill block h-full" style={{ width: `${pct}%` }} />
            </span>
            <span className="whitespace-nowrap text-[10.5px] font-bold text-primary">
              {t('hero.level.label', { n: bl.value })}
            </span>
          </div>
        </div>
        <span aria-hidden="true" className="text-lg text-text-muted">
          ›
        </span>
      </Link>
      {/* The hero's "says" line — the latest fresh voice line (else the authored idle greeting) + a
          "<hero> says" attribution. Embedded as a bare bubble inside this same card (no duplicate avatar:
          the identity row above already shows the face). Owns the live feed + seen-marking. */}
      <HeroPresenceVoice
        archetypeId={degraded ? null : data.archetypeId}
        stageIndex={degraded ? 0 : data.stageIndex}
        look={degraded ? undefined : data.look}
        name={heading}
        embedded
      />
    </div>
  );
}
